import { useState } from 'react';
import { purchasingAPI, GoodsReceiptNote } from '../../../lib/api';
import { Button } from '../../ui/button';
import { X, Check } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../../utils/errorUtils';

interface BulkAcknowledgeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The selected deliveries the per-row Acknowledge tick is offered on. */
  grns: GoodsReceiptNote[];
  onSuccess: () => void;
}

/**
 * CR-8214: confirm dialog for the customer's bulk "Acknowledge Selected".
 *
 * Acknowledges every selected early/late delivery in one submit, with what the
 * per-row Acknowledge tick does — no appeal is raised. One optional comment is
 * shared by all. Rows the server will not acknowledge (acknowledged meanwhile,
 * an appeal decided) come back as skipped and are reported in a toast; the
 * grid refreshes either way, so a stale row never lingers.
 */
export default function BulkAcknowledgeModal({ isOpen, onClose, grns, onSuccess }: BulkAcknowledgeModalProps) {
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const count = grns.length;
  const noun = `deliver${count === 1 ? 'y' : 'ies'}`;

  const handleSubmit = async () => {
    if (count === 0) {
      toast.error('No deliveries selected');
      return;
    }
    try {
      setSubmitting(true);
      const res = await purchasingAPI.bulkAcknowledgeGRNs({
        grn_ids: grns.map((g) => g.id),
        comments: comments.trim() || undefined,
      });
      const { acknowledged_count, skipped_count, skipped } = res.data;
      if (acknowledged_count === 0) {
        // Every row was refused (e.g. acknowledged in another tab meanwhile).
        toast.error('No deliveries acknowledged');
      } else {
        const done = `${acknowledged_count} deliver${acknowledged_count === 1 ? 'y' : 'ies'} acknowledged`;
        toast.success(skipped_count > 0 ? `${done}, ${skipped_count} skipped` : done);
      }
      if (skipped_count > 0 && skipped.length > 0) {
        const reasons = skipped
          .slice(0, 2)
          .map((s) => `${s.grn_number ?? s.grn_id}: ${s.reason}`)
          .join('; ');
        const suffix = skipped.length > 2 ? ` (+${skipped.length - 2} more)` : '';
        toast.warning(`Skipped — ${reasons}${suffix}`);
      }
      await Promise.resolve(onSuccess());
      onClose();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to acknowledge deliveries'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={submitting ? undefined : onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-ack-title"
          className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5 text-green-600" />
              <h3 id="bulk-ack-title" className="text-lg font-bold">Acknowledge Selected Deliveries</h3>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {count} selected
              </span>
            </div>
            <button onClick={onClose} disabled={submitting} aria-label="Close" className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-5 pt-4">
            <p className="text-sm text-gray-600">
              These deliveries will be acknowledged as received, without an appeal. Their on-time
              status stays as it is, and once acknowledged they can no longer be appealed.
            </p>
          </div>

          <div className="px-5 pt-4">
            <p className="text-sm font-semibold text-green-700 mb-2">Selected deliveries</p>
            <div className="border rounded-lg divide-y max-h-52 overflow-y-auto">
              {grns.map((grn) => (
                <div key={String(grn.id)} className="px-3 py-2 text-sm grid grid-cols-4 gap-2">
                  <span className="font-medium truncate" title={grn.grn_number}>{grn.grn_number}</span>
                  <span className="text-gray-600 truncate" title={grn.po_number}>{grn.po_number || '-'}</span>
                  <span className="font-mono text-gray-600 truncate" title={grn.part_number}>{grn.part_number || '-'}</span>
                  <span className="capitalize text-gray-500">{grn.on_time_status || '-'}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-5">
            <label htmlFor="bulk-ack-comments" className="text-sm font-medium block mb-1">Comments (Optional)</label>
            <textarea
              id="bulk-ack-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Added to every acknowledged delivery..."
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 p-5 border-t sticky bottom-0 bg-white">
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || count === 0}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {submitting ? 'Acknowledging...' : `Acknowledge ${count} ${noun.charAt(0).toUpperCase()}${noun.slice(1)}`}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
